// FileUpload component
import React, { useState, useRef, useEffect } from 'react';
import { Upload, File, X, AlertCircle, Download } from 'lucide-react';
import { uploadFile, deleteFile, getFileInfo } from '../../services/fileUploadService';
import { useSession } from '@clerk/clerk-react';
import { updatePlan } from '../../services/divinaPlanesService';
import { useTheme } from '../../contexts/ThemeContext';

const FileUpload = ({
  label,
  value,
  onChange,
  accept = ".pdf",
  maxSize = 10 * 1024 * 1024, // 10MB
  disabled = false,
  planId = null,
  fieldName = null
}) => {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [fileInfo, setFileInfo] = useState(null);
  const [loadingFileInfo, setLoadingFileInfo] = useState(false);
  const { session } = useSession();
  const { themeObject } = useTheme();

  const inputRef = useRef(null);

  const containerTokens = themeObject.fileUpload?.container || {};
  const dropzoneTokens = themeObject.fileUpload?.dropzone || {};
  const fileInfoTokens = themeObject.fileUpload?.fileInfo || {};
  const fileNameTokens = themeObject.fileUpload?.fileName || {};
  const removeButtonTokens = themeObject.fileUpload?.removeButton || {};
  const errorTokens = themeObject.fileUpload?.error || {};
  const uploadingTokens = themeObject.fileUpload?.uploading || {};

  useEffect(() => {
    if (value && session) {
      loadFileInfo();
    } else {
      setFileInfo(null);
    }
  }, [value, session]);

  const loadFileInfo = async () => {
    try {
      setLoadingFileInfo(true);

      // Si value es una URL (comienza con http), es un archivo directo
      if (typeof value === 'string' && value.startsWith('http')) {
        setFileInfo({
          url: value,
          nombre_original: 'Archivo PDF',
          created_at: null,
          isDirectUrl: true
        });
      } else {
        // Si es un ID, obtener info de la BD
        const info = await getFileInfo(value, session);
        if (info.success) {
          setFileInfo(info.archivo);
        } else {
          // Si falla obtener del BD, mantener el ID como referencia
          setFileInfo({
            url: null,
            nombre_original: 'Archivo PDF',
            created_at: null,
            fileId: value
          });
        }
      }
    } catch (err) {
      console.error('Error loading file info:', err);
      setFileInfo({
        url: null,
        nombre_original: 'Archivo PDF',
        created_at: null,
        fileId: value
      });
    } finally {
      setLoadingFileInfo(false);
    }
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (file.size > maxSize) {
      setError(`El archivo es demasiado grande. Máximo ${maxSize / 1024 / 1024}MB`);
      return;
    }

    if (!file.type.includes('pdf')) {
      setError('Solo se permiten archivos PDF');
      return;
    }

    setError(null);
    setUploading(true);

    try {
      if (!session) {
        setError('Debe estar autenticado para subir archivos');
        return;
      }
      
      const result = await uploadFile(file, session);
      
      if (result.success) {
        onChange(result.archivo.id);
        
        if (planId && fieldName) {
          try {
            await updatePlan(planId, {
              [fieldName]: result.archivo.id
            });
          } catch (updateError) {
            console.error('Error al actualizar el plan:', updateError);
            setError('Archivo subido pero error al actualizar el plan');
          }
        }
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError('Error al subir el archivo');
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = async () => {
    if (!value) return;

    setUploading(true);
    try {
      if (!session) {
        setError('Debe estar autenticado para eliminar archivos');
        return;
      }
      
      const result = await deleteFile(value, session);
      if (result.success) {
        onChange(null);
        
        if (planId && fieldName) {
          try {
            await updatePlan(planId, {
              [fieldName]: null
            });
          } catch (updateError) {
            console.error('Error al actualizar el plan:', updateError);
            setError('Archivo eliminado pero error al actualizar el plan');
          }
        }
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError('Error al eliminar el archivo');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div 
      style={{
        display: containerTokens.display,
        flexDirection: containerTokens.flexDirection,
        gap: containerTokens.gap,
        ...(uploading && {
          opacity: uploadingTokens.opacity,
          pointerEvents: uploadingTokens.pointerEvents,
        }),
      }}
    >
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {label}
        </label>
      )}
      
      {!value ? (
        <div
          style={{
            border: dropzoneTokens.border,
            borderRadius: dropzoneTokens.borderRadius,
            padding: dropzoneTokens.padding,
            textAlign: dropzoneTokens.textAlign,
            cursor: disabled ? 'not-allowed' : dropzoneTokens.cursor,
            transition: dropzoneTokens.transition,
          }}
          onClick={() => !disabled && inputRef.current?.click()}
          onDragOver={(e) => {
            e.preventDefault();
            e.currentTarget.style.borderColor = dropzoneTokens['&.dragover']?.borderColor || dropzoneTokens.borderColor;
            e.currentTarget.style.backgroundColor = dropzoneTokens['&.dragover']?.backgroundColor || 'transparent';
          }}
          onDragLeave={(e) => {
            e.currentTarget.style.borderColor = dropzoneTokens.borderColor;
            e.currentTarget.style.backgroundColor = 'transparent';
          }}
          onDrop={(e) => {
            e.preventDefault();
            e.currentTarget.style.borderColor = dropzoneTokens.borderColor;
            e.currentTarget.style.backgroundColor = 'transparent';
            if (!disabled) {
              const files = e.dataTransfer.files;
              if (files.length > 0) {
                handleFileSelect({ target: { files } });
              }
            }
          }}
        >
          <Upload className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <p className="text-sm text-gray-600 mb-2">
            {uploading ? 'Subiendo...' : 'Haz clic para seleccionar un archivo o arrastra y suelta'}
          </p>
          <p className="text-xs text-gray-500">
            Solo archivos PDF, máximo {maxSize / 1024 / 1024}MB
          </p>
          <input
            ref={inputRef}
            type="file"
            accept={accept}
            onChange={handleFileSelect}
            disabled={disabled || uploading}
            className="hidden"
          />
        </div>
      ) : (
        <div
          style={{
            display: fileInfoTokens.display,
            alignItems: fileInfoTokens.alignItems,
            gap: fileInfoTokens.gap,
            padding: fileInfoTokens.padding,
            backgroundColor: fileInfoTokens.backgroundColor,
            borderRadius: fileInfoTokens.borderRadius,
            border: fileInfoTokens.border,
          }}
        >
          <File className="h-4 w-4 text-blue-500" />
          <div style={{ flex: 1 }}>
            <p
              style={{
                fontSize: fileNameTokens.fontSize || '0.875rem',
                color: fileNameTokens.color || '#1f2937',
                fontWeight: '500',
                margin: '0 0 4px 0',
              }}
            >
              {loadingFileInfo ? 'Cargando...' : (fileInfo?.nombre_original || 'Archivo PDF')}
            </p>
            {fileInfo?.created_at && (
              <p style={{ fontSize: '0.75rem', color: '#6b7280', margin: '0' }}>
                Subido: {new Date(fileInfo.created_at).toLocaleDateString('es-ES', {
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </p>
            )}
          </div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            {(fileInfo?.url || fileInfo?.fileId) && (
              <a
                href={`/api/files/pdf/${fileInfo?.fileId || fileInfo?.id}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  color: '#3b82f6',
                  cursor: 'pointer',
                  padding: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  textDecoration: 'none'
                }}
                title="Descargar archivo"
              >
                <Download className="h-4 w-4" />
              </a>
            )}
            <button
              type="button"
              onClick={handleRemove}
              disabled={disabled || uploading}
              style={{
                color: removeButtonTokens.color || '#ef4444',
                cursor: removeButtonTokens.cursor || 'pointer',
                padding: removeButtonTokens.padding || '4px',
                borderRadius: removeButtonTokens.borderRadius,
                border: 'none',
                background: 'none',
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = removeButtonTokens['&:hover']?.backgroundColor || 'rgba(239, 68, 68, 0.1)';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'transparent';
              }}
              title="Eliminar archivo"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {error && (
        <div 
          style={{
            fontSize: errorTokens.fontSize,
            color: errorTokens.color,
            display: errorTokens.display,
            alignItems: errorTokens.alignItems,
            gap: errorTokens.gap,
          }}
        >
          <AlertCircle className="h-4 w-4" />
          <span>{error}</span>
        </div>
      )}
    </div>
  );
};

export default FileUpload;